import { Avatar } from '@app/ui';
import { Button, List, Space, Tooltip } from 'antd';
import { Account, chatifyApi, Icon } from '@webinex/chatify';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface ChatMemberListItemProps {
  id: string;
  chatId: string;
  accounts: Account[] | undefined;
}

function useAdd(chatId: string, accountId: string, withHistory: boolean) {
  const [add, state] = chatifyApi.useAddChatMemberMutation();
  const onAdd = useCallback(
    () => add({ chatId, accountId, withHistory }).unwrap(),
    [add, chatId, accountId, withHistory],
  );
  return [onAdd, state] as const;
}

function useRemove(chatId: string, accountId: string, deleteHistory: boolean) {
  const [remove, state] = chatifyApi.useRemoveChatMemberMutation();
  const onRemove = useCallback(
    () => remove({ chatId, accountId, deleteHistory }).unwrap(),
    [remove, chatId, accountId, deleteHistory],
  );
  return [onRemove, state] as const;
}

export function ChatMemberListItem(props: ChatMemberListItemProps) {
  const { chatId, accounts } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'chats' });
  const { data: chat } = chatifyApi.useGetChatQuery({ id: chatId });
  const account = accounts!.find((x) => x.id === props.id)!;
  const isMember = chat!.members.some((x) => x.id === account.id);
  const [onAddWithoutHistory] = useAdd(chatId, account.id, false);
  const [onAddWithHistory] = useAdd(chatId, account.id, true);
  const [onRemoveKeepHistory] = useRemove(chatId, account.id, false);
  const [onRemoveDeleteHistory] = useRemove(chatId, account.id, true);

  return (
    <List.Item
      className="wxchtf-chat-members-list-item"
      actions={
        isMember
          ? [
              <Tooltip key="remove" title={t('remove')}>
                <Button onClick={onRemoveKeepHistory} type="link" icon={<Icon type="remove-member" />} />
              </Tooltip>,
              <Tooltip key="remove_delete_history" title={t('removeDeleteHistory')}>
                <Button
                  onClick={onRemoveDeleteHistory}
                  type="link"
                  icon={<Icon type="remove-member-delete-history" />}
                />
              </Tooltip>,
            ]
          : [
              <Tooltip key="add_no_history" title={t('add')}>
                <Button onClick={onAddWithoutHistory} type="link" icon={<Icon type="add-member" />} />
              </Tooltip>,
              <Tooltip key="add" title={t('addWithHistory')}>
                <Button
                  onClick={onAddWithHistory}
                  type="link"
                  icon={<Icon type="add-member-with-history" />}
                />
              </Tooltip>,
            ]
      }
    >
      <Space>
        <Avatar size="sm" alt={account.name} reference={account.avatar} />
        <span>{account.name}</span>
      </Space>
    </List.Item>
  );
}
